


import { ReplyAllIcon, RotateCcw } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import { fetchFinalProducts } from '../../slices/company/finalProductSlice';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { fetchBrands } from '../../slices/company/brandSlice';
import { API_URL } from '../../utils/constant';
import Cropper, { ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';


const CamScannerPage = () => {
    const cropperSize = 300; // px
    const cropperRef = useRef(null);
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        serial: '',
        brand: '',
        product: '',
        variant: ''
    });
    const dispatch = useDispatch();
    const { brands } = useSelector((state) => state.brands);
    const { products } = useSelector(state => state.finalProduct);
    const [selectedProduct, setSelectedProduct] = useState([]);
    const [variants, setVariants] = useState([]);
    const [image, setImage] = useState(null);
    const [showCamera, setShowCamera] = useState(true);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [cameraFacingMode, setCameraFacingMode] = useState('environment'); // 'environment' for back, 'user' for front
    const [cropping, setCropping] = useState(false);
    const [crop, setCrop] = useState(0);
    const [croppedImage, setCroppedImage] = useState(null);
    // Fetch brands and products on mount


    useEffect(() => {
        const company = JSON.parse(localStorage.getItem('company'));
        if (company) {
            const companyId = company.company?.id;
            dispatch(fetchBrands(companyId));
            dispatch(fetchFinalProducts(companyId));
        }
    }, [dispatch]);

    // Fetch variants when product changes
    useEffect(() => {
        if (form.product) {
            const selected = products.find(p => p.id == form.product);
            setSelectedProduct(selected);
            // Group variants by variant_name
            // console.log('Grouped Variants:', selected);
            if (selected?.variations) {
                const grouped = Object.values(
                    selected.variations.reduce((acc, v) => {
                        if (!acc[v.variant_name]) acc[v.variant_name] = { ...v, items: [] };
                        acc[v.variant_name].items.push(v);
                        return acc;
                    }, {})
                );
                console.log('Grouped Variants:', grouped);
                setVariants(grouped);
            } else {
                setVariants([]);
            }
        } else {
            setSelectedProduct([]);
            setVariants([]);
        }
    }, [form.product, products]);
    // Handle form change
    const handleChange = async (e) => {
        const { name, value } = e.target;
        if (name === 'brand') {
            setForm(prev => ({ ...prev, brand: value, product: '', variant: '' }));
        } else {
            setForm(prev => ({ ...prev, [name]: value }));
        }

        // If typing in serial, call API to get product/variant
        if (name === 'serial' && value.length > 1) {
            try {
                const company = JSON.parse(localStorage.getItem('company'));
                const res = await fetch(`${API_URL}/final-products/serial-sku/${company.company?.id}?serial_no=${value}`);
                const data = await res.json();
                // API returns { final_product_id, sku, variant_name }
                if (data.final_product_id) {
                    setForm(prev => ({
                        ...prev,
                        brand: data.brand || '',
                        product: data.final_product_id,
                        variant: data.variant_name || ''
                    }));
                } else {
                    setForm(prev => ({
                        ...prev,
                        brand: data.brand || '',
                        product: '',
                        variant: ''
                    }));
                }
            } catch (err) {
                // ignore error
            }
        }
    };

    // Start camera
    const startCamera = async () => {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            let constraints = { video: { facingMode: cameraFacingMode } };
            try {
                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            } catch (err) {
                // fallback to default camera
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            }
        }
    };

    // Restart camera when showCamera becomes true after recapture
    React.useEffect(() => {
        if (step === 2 && showCamera) {
            startCamera();
        }
    }, [showCamera, step]);

    // Capture image
    const captureImage = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (video && canvas) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/png');
            setImage(dataUrl);
            setShowCamera(false);
            setCropping(true);
        }
    };

    // Send data to API
    const handleUpdate = async () => {
        const payload = {
            ...form,
            image: croppedImage || image
        };
        const company = JSON.parse(localStorage.getItem('company'));
        // Replace with your API endpoint
        await fetch(`${API_URL}/final-products/update/image/${company.company?.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        alert('Data updated!');
        setImage(null);
        setShowCamera(true);
        setStep(1);
    };

    return (
        <>
            <h1 className="mb-4 text-center">Import Images</h1>
            <div className="row justify-content-center">
                <div className="col-md-12 col-lg-12">
                    <div className="card mt-4">
                        <div className="card-body">
                            {step === 1 && (
                                <form onSubmit={e => { e.preventDefault(); setStep(2); startCamera(); }}>
                                    <div className="mb-3">
                                        <label className="form-label">Serial No / Article No</label>
                                        <input
                                            type="text"
                                            name="serial"
                                            value={form.serial}
                                            onChange={handleChange}
                                            required
                                            className="form-control"
                                        />
                                    </div>
                                    <div className="text-center my-5 fw-bold text-secondary">--- Or Select ---</div>
                                    <div className="mb-3">
                                        <label className="form-label">Brand</label>
                                        <Select
                                            name="brand"
                                            value={
                                                form.brand === 'other'
                                                    ? { value: 'other', label: 'Other' }
                                                    : brands.find(b => b.id == form.brand)
                                                        ? { value: form.brand, label: brands.find(b => b.id == form.brand)?.name }
                                                        : null
                                            }
                                            onChange={option => handleChange({ target: { name: 'brand', value: option ? option.value : '' } })}
                                            options={[
                                                ...brands.map(b => ({ value: b.id || b, label: b.name || b })),
                                                { value: 'other', label: 'Other' }
                                            ]}
                                            isSearchable
                                            placeholder="Select Brand"
                                            isClearable
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Product Name</label>
                                        <Select
                                            name="product"
                                            value={products.find(p => p.id == form.product) ? { value: form.product, label: products.find(p => p.id == form.product)?.name } : null}
                                            onChange={option => handleChange({ target: { name: 'product', value: option ? option.value : '' } })}
                                            options={
                                                (
                                                    form.brand
                                                        ? (form.brand === 'other'
                                                            ? products.filter(p => p.brand_id == null)
                                                            : products.filter(p => p.brand_id == form.brand)
                                                        )
                                                        : products
                                                ).map(p => ({ value: p.id, label: p.name }))
                                            }
                                            isSearchable
                                            placeholder="Select Product"
                                            isClearable
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Variant</label>
                                        <Select
                                            name="variant"
                                            value={form.variant ? { value: form.variant, label: form.variant } : null}
                                            onChange={option => handleChange({ target: { name: 'variant', value: option ? option.value : '' } })}
                                            options={variants.map(v => ({ value: v.variant_name, label: v.variant_name }))}
                                            isSearchable
                                            placeholder="Select Variant"
                                            isClearable
                                            isDisabled={!form.product}
                                        />
                                    </div>
                                    <button type="submit" className="btn btn-primary w-100">Next</button>
                                </form>
                            )}
                            {step === 2 && (
                                <div>
                                    {showCamera && (
                                        <>
                                            <div className="mb-3">
                                                <video ref={videoRef} autoPlay className="w-100 rounded border" />
                                                <canvas ref={canvasRef} style={{ display: 'none' }} />
                                            </div>
                                            {/* <button onClick={captureImage} className="btn btn-success w-100 mb-3">Capture Image</button> */}
                                                <div className="d-flex justify-content-between gap-3 mb-3">
                                                    <button type="button" className="btn btn-secondary w-100" onClick={() => setCameraFacingMode(f => f === 'environment' ? 'user' : 'environment')}>
                                                        Switch Camera
                                                    </button>
                                                    <button onClick={captureImage} className="btn btn-success w-100">Capture Image</button>
                                                </div>
                                        </>
                                    )}
                                    {image && cropping && (
                                        <div className="mb-3 position-relative d-flex justify-content-center" style={{ minHeight: 350 }}>
                                            <div style={{ width: '100%', maxWidth: 400, height: 350, position: 'relative', margin: '0 auto' }}>
                                                <Cropper
                                                    src={image}
                                                    style={{ height: 350, width: '100%' }}
                                                    initialAspectRatio={1}
                                                    aspectRatio={1}
                                                    guides={true}
                                                    viewMode={1}
                                                    background={false}
                                                    responsive={true}
                                                    autoCropArea={1}
                                                    checkOrientation={false}
                                                    ref={cropperRef}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-primary position-absolute"
                                                    style={{ bottom: 10, right: 10 }}
                                                    onClick={() => {
                                                        const cropper = cropperRef.current?.cropper;
                                                        if (cropper) {
                                                            setCroppedImage(cropper.getCroppedCanvas({ width: cropperSize, height: cropperSize }).toDataURL());
                                                            setCropping(false);
                                                        }
                                                    }}
                                                >
                                                    Crop & Confirm
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {croppedImage && !cropping && (
                                        <div className="mb-3 text-center position-relative">
                                            <img src={croppedImage} alt="Cropped" style={{ width: cropperSize, height: cropperSize, objectFit: 'cover' }} className="rounded border m-auto" />
                                            <button
                                                type="button"
                                                className="btn btn-light position-absolute"
                                                style={{ top: 10, right: 10, borderRadius: '50%', padding: '0.4rem 0.6rem', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}
                                                onClick={() => {
                                                    setImage(null);
                                                    setCroppedImage(null);
                                                    setShowCamera(true);
                                                }}
                                                title="Capture Again"
                                            >
                                                <RotateCcw />
                                            </button>
                                        </div>
                                    )}
                                    <button onClick={handleUpdate} className="btn btn-primary w-100" disabled={!(croppedImage || image)}>Update</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CamScannerPage;